import { capitalizeAll, SelectInputOption } from '@moderepo/mode-app-components';
import { ThresholdComparisonOperator, TimeSeriesAggregation, AlertConditionType, AlertSeverity } from '@moderepo/mode-internal-apis';
import { TFunction } from 'react-i18next';
import { getAlertSeverityDisplayName, getThresholdComparisonOperatorDisplayName } from '..';


// TODO - Update these values to match with back end
export const ALERT_RULE_NAME_LENGTH_MIN = 2;
export const ALERT_RULE_NAME_LENGTH_MAX = 100;
export const ALERT_RULE_DESCRIPTION_LENGTH_MIN = 2;
export const ALERT_RULE_DESCRIPTION_LENGTH_MAX = 200;
export const ALERT_RULE_CONDITION_DEFAULT_DURATION = '1min';
export const ALERT_RULE_CONDITION_DEFAULT_FREQUENCY = '1min';
export const ALERT_RULE_CONDITION_DEFAULT_DELAY = '1min';
export const ALERT_RULE_CONDITION_DEFAULT_SEVERITY = AlertSeverity.MODERATE;
export const ALERT_RULE_CONDITION_DEFAULT_AGGREGATION = TimeSeriesAggregation.AVG;
export const ALERT_RULE_CONDITION_THRESHOLD_OPERAND_MAX = 10;
export const ALERT_RULE_MESSAGING_TITLE_LENGTH_MIN = 2;
export const ALERT_RULE_MESSAGING_TITLE_LENGTH_MAX = 200;
export const ALERT_RULE_MESSAGING_MESSAGE_LENGTH_MIN = 2;
export const ALERT_RULE_MESSAGING_MESSAGE_LENGTH_MAX = 1000;
export const ALERT_RULE_MAX_ACTIONS = 1;
export const ALERT_RULE_MAX_ACTION_RECIPIENTS = 5;


/**
 * Base component interface for all AlertRuleEditor steps. All the Edit Alert Rule step component must extends this interface.
 */
export interface AlertRuleEditorStep {
    readonly projectId: number;
    readonly alertConditionType: AlertConditionType;
    readonly isNewAlertRule: boolean;
    readonly canGoBack: boolean;
    readonly isLastStep: boolean;
    readonly onPrevStepClick: ()=> void;
    readonly onNextStepClick: ()=> void;
    readonly onGotoLastStepClick: ()=> void;
    readonly onStepError: (value: boolean)=> void;
}


/**
 * Helper function to create an Array of Severity options to be used in the Select severity input
 */
export const createSeverityOptions = (trans: TFunction): readonly SelectInputOption<AlertSeverity>[] => {
    return [{
        label: capitalizeAll(trans(getAlertSeverityDisplayName(AlertSeverity.HIGH))),
        value: AlertSeverity.HIGH,
    }, {
        label: capitalizeAll(trans(getAlertSeverityDisplayName(AlertSeverity.MODERATE))),
        value: AlertSeverity.MODERATE,
    }, {
        label: capitalizeAll(trans(getAlertSeverityDisplayName(AlertSeverity.LOW))),
        value: AlertSeverity.LOW,
    }];
};



/**
 * Helper function to create an Array of Threshold Comparison Operator options to be used in the Select operator input
 */
export const createThresholdComparisonOperatorOptions = (trans: TFunction): readonly SelectInputOption<ThresholdComparisonOperator>[] => {
    return [{
        label: capitalizeAll(trans(getThresholdComparisonOperatorDisplayName(ThresholdComparisonOperator.LESS_THAN))),
        value: ThresholdComparisonOperator.LESS_THAN,
    }, {
        label: capitalizeAll(trans(getThresholdComparisonOperatorDisplayName(ThresholdComparisonOperator.LESS_THAN_OR_EQUAL_TO))),
        value: ThresholdComparisonOperator.LESS_THAN_OR_EQUAL_TO,
    }, {
        label: capitalizeAll(trans(getThresholdComparisonOperatorDisplayName(ThresholdComparisonOperator.GREATER_THAN_OR_EQUAL_TO))),
        value: ThresholdComparisonOperator.GREATER_THAN_OR_EQUAL_TO,
    }, {
        label: capitalizeAll(trans(getThresholdComparisonOperatorDisplayName(ThresholdComparisonOperator.GREATER_THAN))),
        value: ThresholdComparisonOperator.GREATER_THAN,
    }, {
        label: capitalizeAll(trans(getThresholdComparisonOperatorDisplayName(ThresholdComparisonOperator.IN))),
        value: ThresholdComparisonOperator.IN,
    }, {
        label: capitalizeAll(trans(getThresholdComparisonOperatorDisplayName(ThresholdComparisonOperator.NOT_IN))),
        value: ThresholdComparisonOperator.NOT_IN,
    }, {
        label: capitalizeAll(trans(getThresholdComparisonOperatorDisplayName(ThresholdComparisonOperator.BETWEEN))),
        value: ThresholdComparisonOperator.BETWEEN,
    }, {
        label: capitalizeAll(trans(getThresholdComparisonOperatorDisplayName(ThresholdComparisonOperator.OUTSIDE))),
        value: ThresholdComparisonOperator.OUTSIDE,
    }];
};


/**
 * Helper function to create an Array of aggregation options to be used in the Select aggregation input
 */
export const createTimeSeriesAggregationOptions = (trans: TFunction): readonly SelectInputOption<TimeSeriesAggregation>[] => {
    return Object.values(TimeSeriesAggregation).map((aggregation) => {
        return {
            label: trans(`alerting.threshold_aggregation.${aggregation.toLowerCase()}`),
            value: aggregation,
        };
    });
};



/**
 * Helper function to create an Array of duration options to be used in the Select duration input
 */
export const createAlertConditionDurationOptions = (trans: TFunction): readonly SelectInputOption<string>[] => {
    return [{
        label: capitalizeAll(trans('label.value_unit_display', { value: 1, unit: trans('units.time.minute', { count: 1 }) })),
        value: '1min',
    }, {
        label: capitalizeAll(trans('label.value_unit_display', { value: 10, unit: trans('units.time.minute', { count: 10 }) })),
        value: '10min',
    }, {
        label: capitalizeAll(trans('label.value_unit_display', { value: 1, unit: trans('units.time.hour', { count: 1 }) })),
        value: '1hour',
    }];
};


/**
 * Helper function to create an Array of delay options to be used in the Select delay input
 */
export const createAlertConditionDelayOptions = (trans: TFunction): readonly SelectInputOption<string>[] => {
    return [{
        label: capitalizeAll(trans('label.value_unit_display', { value: 1, unit: trans('units.time.minute', { count: 1 }) })),
        value: '1min',
    }, {
        label: capitalizeAll(trans('label.value_unit_display', { value: 10, unit: trans('units.time.minute', { count: 10 }) })),
        value: '10min',
    }, {
        label: capitalizeAll(trans('label.value_unit_display', { value: 1, unit: trans('units.time.hour', { count: 1 }) })),
        value: '1hour',
    }];
};
